import React from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Form,
  Table,
} from "components/ui/BootstrapCompat";
import { useQuery } from "react-query";
import useLocalStorage from "helpers/useLocalStorage";
import {
  DonateReferralListItem,
  DonateReferralSummary,
  donateReferralGetList,
  donateReferralGetSummary,
} from "services/main/donate.service";
import { getUserProfileData, userGetMe } from "services/user.service";
import { AFFILIATE_CODE_STORAGE_KEY } from "helpers/authSession";
import { ErrorSwal, SuccessSwal } from "helpers/alertsweet";

type ReferralUser = {
  username?: string;
  affiliateCode?: string;
  referralCode?: string;
};

const numberFormatter = new Intl.NumberFormat("lo-LA");

const getOwnReferralCode = (user: ReferralUser) =>
  String(
    user.affiliateCode ||
      user.referralCode ||
      (user as any).affiliate_code ||
      (user as any).referral_code ||
      (user as any).affiliate?.code ||
      (user as any).affiliate?.affiliateCode ||
      (user as any).referral?.code ||
      (user as any).inviteCode ||
      "",
  ).trim();

const getReferralUrl = (code: string) => {
  const cleanCode = code.trim();
  if (!cleanCode) return "";

  const origin =
    typeof window === "undefined"
      ? "https://merchant.onetop.la"
      : window.location.origin;

  return `${origin}/register?ref=${encodeURIComponent(cleanCode)}`;
};

const getResponseData = <T,>(response: any, fallback: T): T =>
  response?.data?.data || response?.data || response || fallback;

const getResponseList = (response: any): DonateReferralListItem[] => {
  const data = response?.data?.data || response?.data || response;
  return Array.isArray(data) ? data : [];
};

const getResponseCount = (response: any, fallbackCount: number) =>
  Number(response?.data?.count ?? response?.count ?? fallbackCount) || 0;

const toFiniteNumber = (value: unknown) => {
  const numberValue =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatNumber = (value: unknown) =>
  numberFormatter.format(toFiniteNumber(value));

const formatCurrency = (amount: unknown, currency = "LAK") =>
  `${formatNumber(amount)} ${currency}`;

const getUserLabel = (user?: DonateReferralListItem["referredUser"]) =>
  user?.name ||
  user?.username ||
  user?.email ||
  user?.phone ||
  user?._id ||
  "--";

const getReferrerLabel = (item: DonateReferralListItem) =>
  item.referrerUser?.affiliateCode ||
  item.referrerUser?.referralCode ||
  item.referrerUser?.username ||
  item.normalizedReferrer ||
  item.submittedReferrer ||
  "--";

const formatDate = (date?: string) => {
  if (!date) return "--";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "--";

  return parsedDate.toLocaleDateString("lo-LA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getStatusVariant = (status?: string) => {
  if (status === "rewarded" || status === "valid") return "success";
  if (status === "pending") return "warning";
  if (
    status === "invalid" ||
    status === "self_referral" ||
    status === "rejected"
  ) {
    return "danger";
  }
  return "secondary";
};
const getStatusVariantText = (status?: string) => {
  if (status === "rewarded" || status === "valid") return "ຍືນຍັນຂໍ້ມູນແລ້ວ";
  if (status === "pending") return "ລໍຖ້າການກວດສອບ";
  if (
    status === "invalid" ||
    status === "self_referral" ||
    status === "rejected"
  ) {
    return "ບໍ່ຜ່ານການກວດສອບ";
  }
  return "secondary";
};

const copyText = async (text: string, successMessage: string) => {
  if (!text) {
    ErrorSwal("ບໍ່ພົບຂໍ້ມູນສຳລັບ copy");
    return;
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    SuccessSwal(successMessage);
  } catch (error) {
    ErrorSwal("copy ບໍ່ສຳເລັດ");
  }
};

export default function ReferralsPage() {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const [storedAffiliateCode, setStoredAffiliateCode] = useLocalStorage(
    AFFILIATE_CODE_STORAGE_KEY,
    "",
  );
  const pageSize = 10;
  const skip = (currentPage - 1) * pageSize;
  const search = searchTerm.trim();

  const { data: userResponse } = useQuery(
    ["referral-user-me", token, businessId],
    async () => userGetMe({ token, businessId }),
    {
      enabled: Boolean(token),
      retry: false,
    },
  );
  const {
    data: summaryResponse,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useQuery(
    ["referral-creator-summary", token, businessId, search],
    async () =>
      donateReferralGetSummary({
        token,
        businessId,
        search,
      }),
    {
      enabled: Boolean(token),
      retry: false,
    },
  );
  const {
    data: listResponse,
    isLoading,
    isRefetching,
    isError: isListError,
    refetch: refetchList,
  } = useQuery(
    ["referral-creator-list", token, businessId, skip, pageSize, search],
    async () =>
      donateReferralGetList({
        token,
        businessId,
        skip,
        limit: pageSize,
        search,
      }),
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      retry: false,
    },
  );

  const currentUser = getUserProfileData(userResponse) as ReferralUser;
  const ownReferralCode =
    getOwnReferralCode(currentUser) ||
    storedAffiliateCode ||
    currentUser.username ||
    "";
  const ownReferralUrl = getReferralUrl(ownReferralCode);
  const summary = getResponseData<DonateReferralSummary>(summaryResponse, {});
  const referrals = getResponseList(listResponse);
  const totalCount = getResponseCount(listResponse, referrals.length);
  const totalPages = Math.ceil(totalCount / pageSize);
  const currency = summary.currency || referrals[0]?.reward?.currency || "LAK";

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  React.useEffect(() => {
    const responseCode = getOwnReferralCode(currentUser);
    if (responseCode && responseCode !== storedAffiliateCode) {
      setStoredAffiliateCode(responseCode);
    }
  }, [currentUser, setStoredAffiliateCode, storedAffiliateCode]);

  const refetchReferrals = () => {
    refetchSummary();
    refetchList();
  };

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ການຕັ້ງຄ່າ</Breadcrumb.Item>
        <Breadcrumb.Item active>ການແນະນຳໝູ່</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        <Card>
          <Card.Header>ລະຫັດແນະນຳຂອງທ່ານ</Card.Header>
          <Card.Body>
            <div className="rounded border border-[#dbe3ec] bg-white p-3 text-[12px] text-[#475467] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              <div className="mb-2 font-bold text-[#101828] dark:text-slate-100">
                ວິທີແນະນຳ
              </div>
              <div className="space-y-1.5">
                <div>
                  1. Copy link "
                  <span
                    onClick={() => copyText(ownReferralUrl, "copy link ສຳເລັດ")}
                    className="cursor-pointer underline text-primary-500"
                  >
                    {ownReferralUrl || "--"}
                  </span>
                  " ແລ້ວສົ່ງໃຫ້ໝູ່ທາງ WhatsApp, Facebook ຫຼື Line.
                </div>
                <div>2. ໃຫ້ໝູ່ກົດ link ແລະສະໝັກບັນຊີ.</div>
                <div>3. ລະບົບຈະບັນທຶກ code ນີ້ໃນຕອນສະໝັກ.</div>
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                ຂໍ້ມູນນີ້ມາຈາກ donate profile ຂອງຜູ້ໃຊ້ທີ່ login ຢູ່
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຜູ້ທີ່ທ່ານແນະນຳ</Card.Header>
          <Card.Body>
            <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
              {formatNumber(summary.totalReferredUsers)}
              <span className="text-[14px]">ຄົນ</span>
            </h1>
            <div className="text-[12px] text-gray-600">
              valid {formatNumber(summary.validCount)} / pending{" "}
              {formatNumber(summary.pendingReviewCount)}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ລາຍໄດ້ຈາກການແນະນຳໝູ່</Card.Header>
          <Card.Body>
            <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
              {formatCurrency(summary.totalRewardAmount, currency)}
            </h1>
            <div className="text-[12px] text-gray-600">
              pending {formatNumber(summary.pendingReviewCount)} / rewarded{" "}
              {formatNumber(summary.rewardedCount)}
            </div>
            <Link
              to="/referrals/detail"
              className="mt-2 inline-flex text-[12px] font-bold text-primary-500 underline"
            >
              ເບິ່ງລາຍລະອຽດເງື່ອນໄຂ
            </Link>
          </Card.Body>
        </Card>
      </div>

      {(isSummaryError || isListError) && (
        <Alert variant="warning" className="mb-2">
          ໂຫລດຂໍ້ມູນ referral ບໍ່ສຳເລັດ. ກວດສອບ permission ຫຼື response ຈາກ
          /donate/referral/*.
        </Alert>
      )}

      <div className="mb-2 flex flex-wrap gap-2 rounded bg-white p-2 dark:bg-slate-950">
        <Form.Control
          type="text"
          placeholder="ຄົ້ນຫາຊື່, ລະຫັດ, ອີເມວ..."
          className="min-w-0 flex-[1_1_100%] border border-primary md:w-[260px] md:flex-none"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <Button
          variant="primary"
          className="flex flex-1 items-center sm:flex-none"
        >
          <span className="material-icons">search</span>
        </Button>
        <Button
          variant="outline-primary"
          className="flex-1 sm:flex-none"
          onClick={refetchReferrals}
        >
          Refetch
        </Button>
      </div>

      <Card border="primary">
        <Card.Header className="p-1">ລາຍການການແນະນຳໝູ່</Card.Header>
        <div className="overflow-x-auto">
          <Table striped bordered hover size="sm" className="min-w-[920px]">
            <thead>
              <tr>
                <th className="w-[60px] border-b px-3 py-2 text-left">ລ/ດ</th>
                <th className="border-b px-3 py-2 text-left">ຜູ້ຖືກແນະນຳ</th>
                <th className="border-b px-3 py-2 text-left">ລະຫັດທີ່ໃຊ້</th>
                <th className="border-b px-3 py-2 text-left">ລາງວັນ</th>
                <th className="border-b px-3 py-2 text-center">ສະຖານະ</th>
                <th className="border-b px-3 py-2 text-right">ວັນທີສະໝັກ</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isRefetching ? (
                <tr>
                  <td colSpan={6} className="py-4 text-center">
                    ກຳລັງໂຫລດຂໍ້ມູນ...
                  </td>
                </tr>
              ) : referrals.length > 0 ? (
                referrals.map((item, index) => (
                  <tr
                    key={
                      item._id || `${getUserLabel(item.referredUser)}-${index}`
                    }
                  >
                    <td className="border-b px-3 py-2">{skip + index + 1}</td>
                    <td className="border-b px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <div className="font-bold text-primary">
                          {getUserLabel(item.referredUser)}
                        </div>
                        <div className="max-w-[280px] truncate text-[10px] text-neutral-500">
                          {item.referredUser?.email ||
                            item.referredUser?.phone ||
                            item.referredUser?._id ||
                            "--"}
                        </div>
                      </div>
                    </td>
                    <td className="border-b px-3 py-2">
                      <div className="font-bold">{getReferrerLabel(item)}</div>
                      <div className="max-w-[240px] truncate text-[10px] text-neutral-500">
                        {item.submittedReferrer || "--"}
                      </div>
                    </td>
                    <td className="border-b px-3 py-2">
                      {formatCurrency(
                        item.reward?.amount,
                        item.reward?.currency || currency,
                      )}
                    </td>
                    <td className="border-b px-3 py-2 text-center">
                      <Badge variant={getStatusVariant(item.status) as any}>
                        {item.status
                          ? getStatusVariantText(item.status)
                          : "ລໍຖ້າການກວດສອບ"}
                      </Badge>
                    </td>
                    <td className="border-b px-3 py-2 text-right">
                      {formatDate(
                        item.referredUser?.createdAt || item.createdAt,
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-4 text-center">
                    ບໍ່ພົບຂໍ້ມູນການແນະນຳໝູ່
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {totalPages > 1 && (
        <div className="mt-2 flex flex-col gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-500">
            ສະແດງ {skip + 1}-{Math.min(skip + pageSize, totalCount)} ຈາກ{" "}
            {totalCount} ລາຍການ
          </div>
          <div className="flex max-w-full gap-1 overflow-x-auto pb-1 sm:pb-0">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            >
              ‹
            </Button>
            <Button variant="primary" size="sm">
              {currentPage}/{totalPages}
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((page) => Math.min(page + 1, totalPages))
              }
            >
              ›
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
